import { FC, useContext, useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCustomTasksApi } from "../../http/customTasks/api";
import { DefaultTaskCategory } from "../../http/defaultTaskCategories/types";
import { useDefaultTasksApi } from "../../http/defaultTasks/api";
import { DefaultTask } from "../../http/defaultTasks/types";
import Button from "../ui/Button";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import { getDeleteAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import SubmitBar from "../ui/SubmitBar";
import Table from "../ui/Table";

const DEFAULT_TASKS_FIELDS = ["id", "name"] as const;
type DefaultTaskField = (typeof DEFAULT_TASKS_FIELDS)[number];
type DefaultTaskSearch = Pick<DefaultTask, DefaultTaskField>;

export type EditSubsidiaryCustomTasksProps = {
  subsidiaryId: string;
  defaultTaskCategory: DefaultTaskCategory;
  onClose: (reload: boolean) => void;
};

const EditSubsidiaryCustomTasks: FC<EditSubsidiaryCustomTasksProps> = ({
  subsidiaryId,
  defaultTaskCategory,
  onClose,
}) => {
  const aborter = useRef(new AbortController());
  const i18n = useI18n();
  const defaultTasksApi = useDefaultTasksApi();
  const customTasksApi = useCustomTasksApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tasks, setTasks] = useState<DefaultTaskSearch[]>(defaultTaskCategory.tasks ?? []);
  const [defaultTaskOptions, setDefaultTaskOptions] = useState<DefaultTaskSearch[]>([]);
  const searchTerm = useRef("");

  const loadOptions = createLoadOptions<DefaultTaskSearch, DefaultTaskField>(
    aborter,
    searchTerm,
    defaultTasksApi.getAdminDefaultTasks,
    { fields: DEFAULT_TASKS_FIELDS },
  );

  useEffect(() => {
    loadOptions(searchTerm.current).then(setDefaultTaskOptions);
  }, []);

  const saveTasks = async () => {
    try {
      await customTasksApi.updateAdminSubsidiaryCustomTasks(
        subsidiaryId,
        defaultTaskCategory.id,
        tasks.map((t) => t.id),
      );
      onClose(true);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog title={i18n.t.customTasks.set} onClose={() => onClose(false)}>
      <FormFieldsContainer>
        <FormField label={i18n.t.tasks.singular}>
          <AsyncSelect
            menuPosition="fixed"
            loadOptions={loadOptions}
            defaultOptions={defaultTaskOptions}
            placeholder={`${i18n.t.common.select}...`}
            getOptionLabel={(x) => x.name}
            isDisabled={isSubmitting}
            getOptionValue={(x) => x.id}
            styles={reactSelectStyles<DefaultTaskSearch>()}
            onChange={(task) => {
              if (task) {
                if (tasks.some((t) => t.id === task.id)) {
                  dispatch(showErrorNotification(i18n, undefined, i18n.t.customTasks.errors.taskAlreadySelected));
                  return;
                }
                setTasks((prev) => [...(prev ?? []), task]);
              }
            }}
            value={null}
          />
        </FormField>
        <Table<DefaultTaskSearch>
          head={
            <>
              <th align="left">{i18n.t.common.name}</th>
              <th></th>
            </>
          }
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
            </>
          )}
          rowActions={(item) => [
            getDeleteAction(i18n, () => setTasks((prev) => prev.filter((x) => x.id !== item.id) ?? [])),
          ]}
          items={tasks}
        />
      </FormFieldsContainer>
      <SubmitBar>
        <Button
          buttonProps={{
            onClick: () => onClose(false),
            disabled: isSubmitting,
          }}
          type="secondary"
        >
          {i18n.t.common.cancel}
        </Button>
        <Button
          buttonProps={{
            onClick: saveTasks,
            disabled: isSubmitting,
          }}
        >
          {i18n.t.common.save}
        </Button>
      </SubmitBar>
    </ModalDialog>
  );
};

export default EditSubsidiaryCustomTasks;
